import {useCallback, useMemo, type SyntheticEvent, type ReactNode} from 'react'
import {Typography, Box, Select, MenuItem, Checkbox, FormControlLabel, ListSubheader} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {setExportSize, setExportFrameEnabled, selectActiveScene} from '../../../common/components/store/scenesSlice'
import {sealUndo} from '../../../common/undoable'
import {EXPORT_PRESETS, EXPORT_PRESET_GROUPS, CUSTOM_PRESET_ID} from '../../../common/constants/exportPresets'
import type {ExportPreset} from '../../../common/constants/exportPresets'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

const findPresetId = (w: number, h: number): string => {
  const match = EXPORT_PRESETS.find((p) => p.width === w && p.height === h)
  return match ? match.id : CUSTOM_PRESET_ID
}

const groupedPresetItems = (): ReactNode[] => {
  const items: ReactNode[] = []
  for (const group of EXPORT_PRESET_GROUPS) {
    items.push(
      <ListSubheader key={`group-${group}`} sx={{fontSize: 11, lineHeight: '28px'}}>
        {group}
      </ListSubheader>,
    )
    const presets = EXPORT_PRESETS.filter((p) => p.group === group)
    for (const preset of presets) {
      items.push(
        <MenuItem key={preset.id} value={preset.id} sx={{fontSize: 12}}>
          {preset.label} ({preset.width}×{preset.height})
        </MenuItem>,
      )
    }
  }
  return items
}

// Free editor: preset-only export resolution (no custom width/height inputs)
const FreeExportResolutionPanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const exportWidth = useAppSelector((s) => selectActiveScene(s).exportWidth)
  const exportHeight = useAppSelector((s) => selectActiveScene(s).exportHeight)
  const exportFrameEnabled = useAppSelector((s) => selectActiveScene(s).exportFrameEnabled)

  const selectedPresetId = findPresetId(exportWidth, exportHeight)

  const handlePresetChange = useCallback(
    (e: {target: {value: string}}) => {
      const id = e.target.value
      if (id === CUSTOM_PRESET_ID) return
      const preset = EXPORT_PRESETS.find((p: ExportPreset) => p.id === id)
      if (preset) {
        dispatch(setExportSize({width: preset.width, height: preset.height}))
      }
    },
    [dispatch],
  )

  const handleFrameToggle = useCallback(
    (_: SyntheticEvent, checked: boolean) => {
      dispatch(setExportFrameEnabled(checked))
      dispatch(sealUndo())
    },
    [dispatch],
  )

  const sx = useMemo(() => buildStyles(tokens), [tokens])
  const presetItems = useMemo(() => groupedPresetItems(), [])

  return (
    <Box sx={sx.content}>
      <Select
        value={selectedPresetId}
        onChange={handlePresetChange}
        size="small"
        fullWidth
        sx={sx.select}
        MenuProps={{PaperProps: {sx: {maxHeight: 320}}}}
      >
        <MenuItem value={CUSTOM_PRESET_ID} sx={{fontSize: 12}} disabled>
          {t('exportResolution.custom')}
        </MenuItem>
        {presetItems}
      </Select>

      <FormControlLabel
        control={<Checkbox checked={exportFrameEnabled} onChange={handleFrameToggle} size="small" sx={sx.checkbox} />}
        label={<Typography sx={sx.checkboxLabel}>{t('exportResolution.exportFrame')}</Typography>}
        sx={sx.formControl}
      />
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  content: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  select: {
    fontSize: 12,
    color: tokens.text.primary,
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.accordion.border,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: tokens.text.muted,
    },
  },
  checkbox: {
    color: tokens.text.muted,
    '&.Mui-checked': {
      color: tokens.text.primary,
    },
  },
  checkboxLabel: {
    fontSize: 12,
    color: tokens.text.secondary,
  },
  formControl: {
    marginLeft: '-4px',
  },
})

export default FreeExportResolutionPanel
